import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

import { CardVariant } from '@src/components/product-card/product-card.types'
import Product from '@src/quince-site-common/src/domains/Product'
import BaseProduct from '@src/quince-site-common/src/domains/Product/BaseProduct'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductCardDomain from '@src/quince-site-common/src/domains/ProductCard'
import ProductOptionValue, { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import { getPathName } from '@src/quince-site-common/src/utils/getPathName'
import { getPathParams } from '@src/quince-site-common/src/utils/getPathParam'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { isEmpty } from '@src/utils/functions/utils'

import { getInitialSelectedOptions as getInitialSelectedOptionsNewImplementation } from './product-card-context/get-initial-selected-options.utils'

export const QUERY_PARAM_TRACKER = 'tracker'
export const QUERY_PARAM_SEARCH_QUERY = 'searchQuery'
export const QUERY_PARAM_PRODUCT_POSITION = 'productPosition'
export const QUERY_PARAM_VALUE_LANDING_PAGE_SEARCH_SECTION_SEARCH_RESULTS =
  'landingPage__search_section__search_results'

export const makePriceDisplayValue = (price: number, fixed = 2) => {
  return `$${price.toFixed(fixed)}`
}

export const getDiscountedPrice = (originalPrice: string | number, discount: string | number): string => {
  return formatDisplayAmountValue(+originalPrice - (+originalPrice * +discount) / 100)
}

export const getFilteredOptionValueList = (
  optionValues: ProductOptionValue[],
  selectedValue: string,
  maxOptions?: number,
) => {
  if (!maxOptions) return optionValues
  if (optionValues.length <= maxOptions) {
    return optionValues
  }
  const index = optionValues.findIndex(optionValue => optionValue.value === selectedValue)
  const filteredOptions = optionValues.slice(0, maxOptions)
  if (index >= maxOptions && filteredOptions[index]) {
    filteredOptions[maxOptions - 1] = filteredOptions[index]
  }
  return filteredOptions
}

export const isNestedProduct = (product: BaseProduct | Bundle) => {
  const isNestedProduct = !!(product.childShopifyProductIds && product.childShopifyProductIds.length > 0)
  return isNestedProduct
}

// TODO: Pass single object in this function as arg
export const getInitialSelectedOptions = (
  product: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[] = [],
  fillAllOptions = false,
  ignoreVariantsVerification = false,
  defaultFillCount = 1,
) => {
  return getInitialSelectedOptionsNewImplementation({
    product,
    selectedOptions,
    fillAllOptions,
    ignoreVariantsVerification,
    defaultFillCount,
  })
}

export const getProductLinkWithSlug = (
  slug: string,
  selectedOptions: ProductVariantOption[],
  tracker: string | undefined = undefined,
  additionalAttributes?: Record<string, string>,
  searchQuery?: string,
  position?: number,
) => {
  const params: Record<string, string> = {}
  const color = getSelectedColorFromSelectedOptions(selectedOptions)
  if (color) {
    params.color = getURLStringFromInputString(color)
  }
  if (tracker) {
    params[QUERY_PARAM_TRACKER] = tracker
  }
  if (searchQuery) {
    params[QUERY_PARAM_SEARCH_QUERY] = searchQuery
  }
  if (position) {
    params[QUERY_PARAM_PRODUCT_POSITION] = position.toString()
  }

  if (additionalAttributes && !isEmpty(additionalAttributes)) {
    Object.entries(additionalAttributes).forEach(([value, key]) => (params[key] = value.toLowerCase()))
  }

  let currentPageSlug = ''
  if (isBrowser()) {
    currentPageSlug = window.location.pathname
  }
  return getPathName(slug) + getPathParams(params, currentPageSlug)
}

export const getProductLink = (
  product: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[],
  tracker: string | undefined = undefined,
  additionalAttributes?: Record<string, string>,
  searchQuery?: string,
  position?: number,
) => {
  return getProductLinkWithSlug(product.slug, selectedOptions, tracker, additionalAttributes, searchQuery, position)
}

export const getProductLinkWithSelectedColor = (
  product: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[],
  ignoreDefaultColor?: boolean,
) => {
  const params: Record<string, string> = {}
  if (!ignoreDefaultColor || !isDefaultColorSelected(product, selectedOptions)) {
    const selectedColor = getSelectedColorFromSelectedOptions(selectedOptions)
    if (selectedColor) {
      params.color = getURLStringFromInputString(selectedColor)
    }
  }

  return getPathName(product.slug) + getPathParams(params, '')
}

const isDefaultColorSelected = (product: BaseProduct | Bundle, selectedOptions: ProductVariantOption[]) => {
  const defaultProductColor = getDefaultProductColor(product)
  const selectedColorName = selectedOptions.find(option => option.name === ProductOptionName.Color)?.value
  if (defaultProductColor && selectedColorName) {
    return defaultProductColor === selectedColorName
  }
  return false
}

const getDefaultProductColor = (product: BaseProduct | Bundle) => {
  const productColorOptions = product.productOptions.find(option => option.name === ProductOptionName.Color)?.values
  if (productColorOptions?.length) {
    const firstColorOptionName = productColorOptions[0]?.value
    return firstColorOptionName
  }
  return
}

export const getSelectedColorFromSelectedOptions = (selectedOptions: ProductVariantOption[] = []) => {
  return selectedOptions.find(option => option.name.toLowerCase() == 'color')?.value
}

export const getSelectedSizeFromSelectedOptions = (selectedOptions: ProductVariantOption[]) => {
  return selectedOptions.find(option => option.name.toLowerCase() == 'size')?.value
}

export const getSelectedOptionFromSelectedOptions = (selectedOptions: ProductVariantOption[]) => {
  const productOptionNames = getProductOptionNamesFromSelectedOptions(selectedOptions)
  const options = productOptionNames.map(name => selectedOptions.find(option => (option.name as string) == name)?.value)
  if (!isEmpty(options)) {
    return options[0]
  }
  return undefined
}

export const getProductOptionNamesFromSelectedOptions = (selectedOptions: ProductVariantOption[]): string[] => {
  let optionNames = selectedOptions.map(o => o.name)
  optionNames = optionNames.filter(name => !(name in ProductOptionName))
  return optionNames
}

export const getProductOptionNamesFromProduct = (product: Product | Bundle): string[] => {
  let optionNames = product.productOptions.map(o => o.name)
  optionNames = optionNames.filter(name => !(name in ProductOptionName))
  return optionNames
}

export const navigateToPDP = ({
  product,
  tracker,
  selectedOptions,
  additionalAttributes,
  router,
}: {
  product: ProductCardDomain['product']
  tracker: string | undefined
  selectedOptions: ProductCardDomain['selectedOptions']
  additionalAttributes?: Record<string, string>
  router: AppRouterInstance
}) => {
  console.log('>>>>> old navigate to pdp tracker: ', tracker)
  const productLink = getProductLink(product, selectedOptions, tracker, additionalAttributes)
  router.push(productLink)
}

export type ProductTagKey =
  | 'Back in stock'
  | 'Sold Out'
  | 'Best seller'
  | 'New'
  | 'Low Stock'
  | 'Leaving Soon'
  | 'Instock'
  | 'Sold Recently'

export const getIndividualTagStatus = (
  tags: ProductTagKey[],
): {
  is_new: 1 | 0
  back_in_stock: 1 | 0
  sold_out: 1 | 0
  low_stock: 1 | 0
  best_seller: 1 | 0
  instock: 1 | 0
  sold_recently: 1 | 0
  leaving_soon: 1 | 0
} => {
  return {
    is_new: tags.includes('New') ? 1 : 0,
    back_in_stock: tags.includes('Back in stock') ? 1 : 0,
    sold_out: tags.includes('Sold Out') ? 1 : 0,
    low_stock: tags.includes('Low Stock') ? 1 : 0,
    best_seller: tags.includes('Best seller') ? 1 : 0,
    instock: tags.includes('Instock') ? 1 : 0,
    sold_recently: tags.includes('Sold Recently') ? 1 : 0,
    leaving_soon: tags.includes('Leaving Soon') ? 1 : 0,
  }
}

export const getProductTagsList = (
  applicableTags: ProductTag[],
  selectedOptions?: ProductVariantOption[],
): ProductTagKey[] => {
  return applicableTags
    .filter(
      tag =>
        Array.isArray(tag.options) &&
        (tag.options.some(
          opt => opt.value === selectedOptions?.find(so => so.name === ProductOptionName.Color)?.value,
        ) ||
          tag.options.length === 0),
    )
    .map(tag => tag.value as ProductTagKey)
}

export const SocialProofTagsMappingInTracker = {
  stockTag: 'Instock',
  soldRecentlyTag: 'Sold Recently',
}

export const getProductTagsForUrgencyTagsForTracking = (selectedVariant?: CardVariant): ProductTagKey[] => {
  return Object.entries(selectedVariant?.socialProofData ?? {}).reduce<string[]>((acc, [key, value]) => {
    if (value) {
      acc.push(SocialProofTagsMappingInTracker[key as keyof typeof SocialProofTagsMappingInTracker])
    }
    return acc
  }, []) as ProductTagKey[]
}

export const getUrgencyTagsInventoryForTracking = (selectedVariant?: CardVariant): Record<string, number> => {
  return Object.entries(selectedVariant?.socialProofData ?? {}).reduce<Record<string, number>>((acc, [key, value]) => {
    if (value) {
      acc[SocialProofTagsMappingInTracker[key as keyof typeof SocialProofTagsMappingInTracker]] = Number(
        value.match(/\d+/)?.[0], // Extracts the number from the string
      )
    }
    return acc
  }, {})
}
